import DashboardLayout from '@/components/DashboardLayout';
import { router } from '@inertiajs/react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ImageUpload } from '@/components/ui/image-upload';
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  ArrowLeft, CalendarDays, MapPin, Users, DollarSign, Trophy,
  Send, XCircle, Medal, Crown, Check, X, Heart, Plus, Trash2, Instagram, Globe, ChevronsUpDown,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useState } from 'react';
import { toast } from 'sonner';

interface UserRef { id: number; name: string; }
interface CategoryRef { id: number; name: string; }

interface Registration {
  id: number;
  user: UserRef;
  status: string;
  paid: boolean;
  category: CategoryRef | null;
  created_at: string;
}

interface PodiumEntry {
  id: number;
  position: number;
  position_label: string;
  user: UserRef;
  category: CategoryRef | null;
}

interface Sponsor {
  id: number;
  name: string;
  instagram: string | null;
  website: string | null;
  logo_url: string | null;
}

interface Category {
  id: number;
  name: string;
  capacity: number | null;
  registrations_count: number;
  available_spots: number | null;
  is_full: boolean;
}

interface EventData {
  id: number;
  title: string;
  description: string | null;
  event_type: string;
  location: string | null;
  start_date: string;
  end_date: string | null;
  registration_deadline: string | null;
  capacity: number | null;
  registrations_count: number;
  available_spots: number | null;
  price_cents: number;
  formatted_price: string;
  is_free: boolean;
  requires_payment: boolean;
  requires_approval: boolean;
  status: string;
  registration_open: boolean;
  is_full: boolean;
  banner_url: string | null;
}

interface Props {
  event: EventData;
  registrations: Registration[];
  podium: PodiumEntry[];
  sponsors: Sponsor[];
  categories: Category[];
  registered_student_ids: number[];
  students: UserRef[];
}

const STATUS_MAP: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  draft: { label: 'Rascunho', variant: 'secondary' },
  published: { label: 'Publicado', variant: 'default' },
  closed: { label: 'Encerrado', variant: 'outline' },
  cancelled: { label: 'Cancelado', variant: 'destructive' },
};

const REG_STATUS: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  pending_approval: { label: 'Aguardando', variant: 'secondary' },
  approved: { label: 'Aprovado', variant: 'default' },
  rejected: { label: 'Rejeitado', variant: 'destructive' },
  cancelled: { label: 'Cancelado', variant: 'outline' },
};

const TYPE_MAP: Record<string, string> = {
  trip: 'Trip', tournament: 'Torneio', workshop: 'Workshop', party: 'Festa', other: 'Outro',
};

const PODIUM_ICONS = [Crown, Medal, Medal];
const PODIUM_COLORS = ['text-yellow-500', 'text-gray-400', 'text-amber-700'];

export default function Show({ event, registrations, podium, sponsors, categories, registered_student_ids, students }: Props) {
  const [actionDialog, setActionDialog] = useState<string | null>(null);

  // Podio
  const [podiumPosition, setPodiumPosition] = useState('');
  const [podiumUser, setPodiumUser] = useState('');
  const [podiumCategory, setPodiumCategory] = useState('');

  // Sponsor
  const [sponsorOpen, setSponsorOpen] = useState(false);
  const [sponsorName, setSponsorName] = useState('');
  const [sponsorInstagram, setSponsorInstagram] = useState('');
  const [sponsorWebsite, setSponsorWebsite] = useState('');
  const [sponsorLogo, setSponsorLogo] = useState<File | null>(null);

  // Category
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [categoryName, setCategoryName] = useState('');
  const [categoryCapacity, setCategoryCapacity] = useState('');

  // Inscricao manual
  const [registerOpen, setRegisterOpen] = useState(false);
  const [registerUserId, setRegisterUserId] = useState('');
  const [registerCategoryId, setRegisterCategoryId] = useState('');
  const [studentSearchOpen, setStudentSearchOpen] = useState(false);

  const statusInfo = STATUS_MAP[event.status] || STATUS_MAP.draft;
  const isTournament = event.event_type === 'tournament';

  const handleAction = (action: string) => {
    router.patch(`/events/${event.id}/${action}`, {}, {
      onSuccess: () => setActionDialog(null),
    });
  };

  const addPodium = () => {
    if (!podiumPosition || !podiumUser) { toast.error('Selecione posicao e aluno'); return; }
    router.post(`/events/${event.id}/podium`, {
      event_podium: {
        position: parseInt(podiumPosition),
        user_id: parseInt(podiumUser),
        event_category_id: podiumCategory || null,
      }
    }, { onSuccess: () => { setPodiumPosition(''); setPodiumUser(''); setPodiumCategory(''); } });
  };

  const removePodium = (podiumId: number) => {
    router.delete(`/events/${event.id}/podium/${podiumId}`);
  };

  const addSponsor = () => {
    if (!sponsorName) { toast.error('Nome do patrocinador e obrigatorio'); return; }
    const formData = new FormData();
    formData.append('event_sponsor[name]', sponsorName);
    if (sponsorInstagram) formData.append('event_sponsor[instagram]', sponsorInstagram);
    if (sponsorWebsite) formData.append('event_sponsor[website]', sponsorWebsite);
    if (sponsorLogo) formData.append('event_sponsor[logo]', sponsorLogo);
    router.post(`/events/${event.id}/sponsors`, formData, {
      forceFormData: true,
      onSuccess: () => { setSponsorOpen(false); setSponsorName(''); setSponsorInstagram(''); setSponsorWebsite(''); setSponsorLogo(null); },
    });
  };

  const removeSponsor = (sponsorId: number) => {
    router.delete(`/events/${event.id}/sponsors/${sponsorId}`);
  };

  const addCategory = () => {
    if (!categoryName) { toast.error('Nome da categoria e obrigatorio'); return; }
    router.post(`/events/${event.id}/categories`, {
      event_category: { name: categoryName, capacity: categoryCapacity ? parseInt(categoryCapacity) : null }
    }, { onSuccess: () => { setCategoryOpen(false); setCategoryName(''); setCategoryCapacity(''); } });
  };

  const removeCategory = (categoryId: number) => {
    router.delete(`/events/${event.id}/categories/${categoryId}`);
  };

  const approveRegistration = (regId: number) => {
    router.patch(`/events/${event.id}/registrations/${regId}/approve`);
  };

  const rejectRegistration = (regId: number) => {
    router.patch(`/events/${event.id}/registrations/${regId}/reject`);
  };

  const manualRegister = () => {
    if (!registerUserId) { toast.error('Selecione um aluno'); return; }
    router.post(`/events/${event.id}/registrations`, {
      event_registration: {
        user_id: parseInt(registerUserId),
        event_category_id: registerCategoryId || null,
      }
    }, {
      onSuccess: () => { setRegisterOpen(false); setRegisterUserId(''); setRegisterCategoryId(''); },
    });
  };

  const podiumUserIds = podium.map(p => p.user.id);

  return (
    <DashboardLayout>
      <div className="p-8 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => router.visit('/events')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold">{event.title}</h1>
              <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
              <Badge variant="outline">{TYPE_MAP[event.event_type]}</Badge>
              {event.requires_approval && <Badge variant="secondary">Aprovacao manual</Badge>}
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => router.visit(`/events/${event.id}/edit`)}>Editar</Button>
            {event.status === 'draft' && (
              <Button size="sm" onClick={() => setActionDialog('publish')}><Send className="w-4 h-4 mr-1" /> Publicar</Button>
            )}
            {event.status === 'published' && (
              <Button size="sm" variant="outline" onClick={() => setActionDialog('close')}>Encerrar</Button>
            )}
            {event.status !== 'cancelled' && (
              <Button size="sm" variant="destructive" onClick={() => setActionDialog('cancel')}><XCircle className="w-4 h-4 mr-1" /></Button>
            )}
          </div>
        </div>

        {/* Banner */}
        {event.banner_url && (
          <div className="rounded-xl overflow-hidden mb-6 h-48">
            <img src={event.banner_url} alt={event.title} className="w-full h-full object-cover" />
          </div>
        )}

        <Tabs defaultValue="info">
          <TabsList>
            <TabsTrigger value="info">Informacoes</TabsTrigger>
            <TabsTrigger value="registrations">
              Inscricoes <Badge variant="secondary" className="ml-1.5 text-xs">{registrations.filter(r => r.status !== 'cancelled').length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="sponsors">
              Patrocinadores {sponsors.length > 0 && <Badge variant="secondary" className="ml-1.5 text-xs">{sponsors.length}</Badge>}
            </TabsTrigger>
            {isTournament && (
              <>
                <TabsTrigger value="categories">
                  Categorias {categories.length > 0 && <Badge variant="secondary" className="ml-1.5 text-xs">{categories.length}</Badge>}
                </TabsTrigger>
                <TabsTrigger value="podium"><Trophy className="w-4 h-4 mr-1" /> Podio</TabsTrigger>
              </>
            )}
          </TabsList>

          {/* ── INFO ── */}
          <TabsContent value="info" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <Card>
                <CardContent className="p-4 flex items-center gap-3">
                  <CalendarDays className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Data</p>
                    <p className="font-medium text-sm">
                      {format(new Date(event.start_date), "dd 'de' MMM, yyyy", { locale: ptBR })}
                      {event.end_date && ` - ${format(new Date(event.end_date), "dd 'de' MMM", { locale: ptBR })}`}
                    </p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 flex items-center gap-3">
                  <Users className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Inscricoes</p>
                    <p className="font-medium text-sm">{event.registrations_count}{event.capacity ? ` / ${event.capacity}` : ''}</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 flex items-center gap-3">
                  <DollarSign className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Preco</p>
                    <p className="font-medium text-sm">{event.formatted_price}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
            {event.location && (
              <p className="flex items-center gap-2 text-sm text-muted-foreground mb-4"><MapPin className="w-4 h-4" />{event.location}</p>
            )}
            {event.description && (
              <Card><CardContent className="p-4"><p className="whitespace-pre-wrap text-sm">{event.description}</p></CardContent></Card>
            )}
          </TabsContent>

          {/* ── INSCRICOES ── */}
          <TabsContent value="registrations" className="mt-6 space-y-4">
            <div className="flex justify-end">
              <Button size="sm" variant="outline" onClick={() => setRegisterOpen(true)}>
                <Plus className="w-4 h-4 mr-1" /> Inscrever Aluno
              </Button>
            </div>
            <Card>
              <CardContent className="p-0">
                {registrations.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground"><Users className="w-10 h-10 mx-auto mb-3 opacity-30" /><p>Nenhuma inscricao</p></div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Aluno</TableHead>
                        {isTournament && categories.length > 0 && <TableHead>Categoria</TableHead>}
                        <TableHead>Status</TableHead>
                        <TableHead>Pagamento</TableHead>
                        <TableHead>Data</TableHead>
                        {event.requires_approval && <TableHead className="text-right">Acoes</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {registrations.map((reg) => {
                        const regStatus = REG_STATUS[reg.status] || REG_STATUS.pending_approval;
                        return (
                          <TableRow key={reg.id}>
                            <TableCell className="font-medium">{reg.user.name}</TableCell>
                            {isTournament && categories.length > 0 && (
                              <TableCell>{reg.category ? <Badge variant="outline">{reg.category.name}</Badge> : '-'}</TableCell>
                            )}
                            <TableCell><Badge variant={regStatus.variant}>{regStatus.label}</Badge></TableCell>
                            <TableCell>
                              {event.requires_payment ? (
                                reg.paid ? <Badge variant="default">Pago</Badge> : <Badge variant="secondary">Pendente</Badge>
                              ) : <span className="text-xs text-muted-foreground">Gratuito</span>}
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">{format(new Date(reg.created_at), 'dd/MM/yy HH:mm', { locale: ptBR })}</TableCell>
                            {event.requires_approval && (
                              <TableCell className="text-right">
                                {(reg.status === 'pending_approval' || reg.status === 'rejected') && (
                                  <div className="flex justify-end gap-1">
                                    <Button size="sm" variant="ghost" className="h-7 text-green-600" onClick={() => approveRegistration(reg.id)}>
                                      <Check className="w-4 h-4 mr-1" /> Aprovar
                                    </Button>
                                    {reg.status !== 'rejected' && (
                                      <Button size="sm" variant="ghost" className="h-7 text-red-600" onClick={() => rejectRegistration(reg.id)}>
                                        <X className="w-4 h-4 mr-1" /> Rejeitar
                                      </Button>
                                    )}
                                  </div>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── PATROCINADORES ── */}
          <TabsContent value="sponsors" className="mt-6 space-y-4">
            <div className="flex justify-end">
              <Button size="sm" variant="outline" onClick={() => setSponsorOpen(true)}>
                <Plus className="w-4 h-4 mr-1" /> Adicionar Patrocinador
              </Button>
            </div>
            {sponsors.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground"><Heart className="w-10 h-10 mx-auto mb-3 opacity-30" /><p>Nenhum patrocinador</p></div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {sponsors.map((s) => (
                  <Card key={s.id}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        {s.logo_url ? (
                          <img src={s.logo_url} alt={s.name} className="w-12 h-12 rounded-lg object-contain border" />
                        ) : (
                          <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center"><Heart className="w-5 h-5 text-muted-foreground/30" /></div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm">{s.name}</p>
                          <div className="flex items-center gap-3 mt-1">
                            {s.instagram && <span className="flex items-center gap-1 text-xs text-muted-foreground"><Instagram className="w-3 h-3" />{s.instagram}</span>}
                            {s.website && <span className="flex items-center gap-1 text-xs text-muted-foreground"><Globe className="w-3 h-3" />Site</span>}
                          </div>
                        </div>
                        <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => removeSponsor(s.id)}>
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ── CATEGORIAS (torneio) ── */}
          {isTournament && (
            <TabsContent value="categories" className="mt-6 space-y-4">
              <div className="flex justify-end">
                <Button size="sm" variant="outline" onClick={() => setCategoryOpen(true)}>
                  <Plus className="w-4 h-4 mr-1" /> Adicionar Categoria
                </Button>
              </div>
              {categories.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground"><Trophy className="w-10 h-10 mx-auto mb-3 opacity-30" /><p>Nenhuma categoria</p></div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {categories.map((c) => (
                    <Card key={c.id}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{c.name}</p>
                            <p className="text-sm text-muted-foreground mt-1">
                              {c.registrations_count}{c.capacity ? ` / ${c.capacity} vagas` : ' inscritos'}
                              {c.is_full && <Badge variant="destructive" className="ml-2 text-xs">Lotada</Badge>}
                            </p>
                          </div>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeCategory(c.id)}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          )}

          {/* ── PODIO (torneio) ── */}
          {isTournament && (
            <TabsContent value="podium" className="mt-6 space-y-6">
              {categories.length > 0 ? (
                // Podio por categoria
                categories.map((cat) => {
                  const catPodium = podium.filter(p => p.category?.id === cat.id);
                  const occupiedPositions = catPodium.map(p => p.position);
                  return (
                    <Card key={cat.id}>
                      <CardHeader><CardTitle className="text-base">{cat.name}</CardTitle></CardHeader>
                      <CardContent>
                        <div className="flex justify-center gap-6 mb-4">
                          {[1, 2, 3].map((pos) => {
                            const entry = catPodium.find(p => p.position === pos);
                            const Icon = PODIUM_ICONS[pos - 1];
                            const color = PODIUM_COLORS[pos - 1];
                            return (
                              <div key={pos} className="text-center">
                                <div className={`w-16 h-16 rounded-full border-2 flex items-center justify-center mx-auto mb-1 ${entry ? 'border-primary bg-primary/5' : 'border-dashed border-muted-foreground/30'}`}>
                                  {entry ? <span className="text-sm font-bold">{entry.user.name.split(' ').map(n => n[0]).join('').slice(0, 2)}</span> : <Icon className={`w-6 h-6 ${color} opacity-30`} />}
                                </div>
                                <Icon className={`w-4 h-4 mx-auto ${color}`} />
                                <p className="text-xs font-medium mt-0.5">{entry?.user.name || '-'}</p>
                                <p className="text-[10px] text-muted-foreground">{pos}o lugar</p>
                                {entry && <Button variant="ghost" size="sm" className="mt-0.5 h-5 text-[10px] text-destructive" onClick={() => removePodium(entry.id)}>Remover</Button>}
                              </div>
                            );
                          })}
                        </div>
                        {occupiedPositions.length < 3 && (
                          <div className="flex gap-2 items-end">
                            <div className="flex-1">
                              <Select value={podiumCategory === String(cat.id) ? podiumPosition : ''} onValueChange={(v) => { setPodiumPosition(v); setPodiumCategory(String(cat.id)); }}>
                                <SelectTrigger><SelectValue placeholder="Posicao" /></SelectTrigger>
                                <SelectContent>
                                  {[1, 2, 3].filter(p => !occupiedPositions.includes(p)).map(p => <SelectItem key={p} value={String(p)}>{p}o lugar</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="flex-1">
                              <Select value={podiumCategory === String(cat.id) ? podiumUser : ''} onValueChange={(v) => { setPodiumUser(v); setPodiumCategory(String(cat.id)); }}>
                                <SelectTrigger><SelectValue placeholder="Aluno" /></SelectTrigger>
                                <SelectContent>
                                  {students.filter(s => registered_student_ids.includes(s.id) && !podiumUserIds.includes(s.id)).map(s => <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                            <Button size="sm" onClick={() => { setPodiumCategory(String(cat.id)); addPodium(); }}><Trophy className="w-4 h-4" /></Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })
              ) : (
                // Podio sem categorias
                <Card>
                  <CardContent className="p-6">
                    <div className="flex justify-center gap-6 mb-4">
                      {[1, 2, 3].map((pos) => {
                        const entry = podium.find(p => p.position === pos);
                        const Icon = PODIUM_ICONS[pos - 1];
                        const color = PODIUM_COLORS[pos - 1];
                        return (
                          <div key={pos} className="text-center">
                            <div className={`w-20 h-20 rounded-full border-2 flex items-center justify-center mx-auto mb-2 ${entry ? 'border-primary bg-primary/5' : 'border-dashed border-muted-foreground/30'}`}>
                              {entry ? <span className="text-lg font-bold">{entry.user.name.split(' ').map(n => n[0]).join('').slice(0, 2)}</span> : <Icon className={`w-8 h-8 ${color} opacity-30`} />}
                            </div>
                            <Icon className={`w-5 h-5 mx-auto mb-1 ${color}`} />
                            <p className="text-sm font-medium">{entry?.user.name || '-'}</p>
                            <p className="text-xs text-muted-foreground">{pos}o lugar</p>
                            {entry && <Button variant="ghost" size="sm" className="mt-1 h-6 text-xs text-destructive" onClick={() => removePodium(entry.id)}>Remover</Button>}
                          </div>
                        );
                      })}
                    </div>
                    {podium.length < 3 && (
                      <div className="flex gap-3 items-end">
                        <div className="flex-1">
                          <Label>Posicao</Label>
                          <Select value={podiumPosition} onValueChange={setPodiumPosition}>
                            <SelectTrigger className="mt-1"><SelectValue placeholder="Selecione" /></SelectTrigger>
                            <SelectContent>
                              {[1, 2, 3].filter(p => !podium.map(x => x.position).includes(p)).map(p => <SelectItem key={p} value={String(p)}>{p}o lugar</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex-1">
                          <Label>Aluno</Label>
                          <Select value={podiumUser} onValueChange={setPodiumUser}>
                            <SelectTrigger className="mt-1"><SelectValue placeholder="Selecione" /></SelectTrigger>
                            <SelectContent>
                              {students.filter(s => registered_student_ids.includes(s.id) && !podiumUserIds.includes(s.id)).map(s => <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <Button onClick={addPodium}><Trophy className="w-4 h-4 mr-1" /> Adicionar</Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Dialog: Acao */}
      <Dialog open={actionDialog !== null} onOpenChange={(open) => { if (!open) setActionDialog(null); }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {actionDialog === 'publish' && 'Publicar Evento'}
              {actionDialog === 'close' && 'Encerrar Evento'}
              {actionDialog === 'cancel' && 'Cancelar Evento'}
            </DialogTitle>
            <DialogDescription>
              {actionDialog === 'publish' && 'O evento ficara visivel para todos os alunos.'}
              {actionDialog === 'close' && 'As inscricoes serao encerradas.'}
              {actionDialog === 'cancel' && 'O evento sera cancelado.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog(null)}>Voltar</Button>
            <Button variant={actionDialog === 'cancel' ? 'destructive' : 'default'} onClick={() => handleAction(actionDialog!)}>Confirmar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Patrocinador */}
      <Dialog open={sponsorOpen} onOpenChange={(open) => { if (!open) setSponsorOpen(false); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Adicionar Patrocinador</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div><Label>Nome *</Label><Input value={sponsorName} onChange={(e) => setSponsorName(e.target.value)} className="mt-1" placeholder="Digite o nome" /></div>
            <div><Label>Instagram</Label><Input value={sponsorInstagram} onChange={(e) => setSponsorInstagram(e.target.value)} className="mt-1" placeholder="@perfil" /></div>
            <div><Label>Website</Label><Input value={sponsorWebsite} onChange={(e) => setSponsorWebsite(e.target.value)} className="mt-1" placeholder="https://" /></div>
            <div><Label>Logo</Label><div className="mt-1"><ImageUpload onImageSelect={setSponsorLogo} /></div></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSponsorOpen(false)}>Cancelar</Button>
            <Button onClick={addSponsor}>Adicionar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Inscricao Manual */}
      <Dialog open={registerOpen} onOpenChange={(open) => { if (!open) setRegisterOpen(false); }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Inscrever Aluno</DialogTitle>
            <DialogDescription>Inscricao manual pelo admin (aprovada automaticamente)</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Aluno *</Label>
              <Popover open={studentSearchOpen} onOpenChange={setStudentSearchOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-between mt-1 font-normal">
                    {registerUserId
                      ? students.find(s => s.id === parseInt(registerUserId))?.name
                      : 'Buscar aluno...'}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Buscar por nome..." />
                    <CommandList>
                      <CommandEmpty>Nenhum aluno encontrado</CommandEmpty>
                      <CommandGroup>
                        {students.filter(s => !registered_student_ids.includes(s.id)).map(s => (
                          <CommandItem
                            key={s.id}
                            value={s.name}
                            onSelect={() => { setRegisterUserId(String(s.id)); setStudentSearchOpen(false); }}
                          >
                            <Check className={`mr-2 h-4 w-4 ${registerUserId === String(s.id) ? 'opacity-100' : 'opacity-0'}`} />
                            {s.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            {isTournament && categories.length > 0 && (
              <div>
                <Label>Categoria</Label>
                <Select value={registerCategoryId} onValueChange={setRegisterCategoryId}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Selecione a categoria" /></SelectTrigger>
                  <SelectContent>
                    {categories.map(c => (
                      <SelectItem key={c.id} value={String(c.id)}>{c.name} {c.capacity ? `(${c.registrations_count}/${c.capacity})` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegisterOpen(false)}>Cancelar</Button>
            <Button onClick={manualRegister}>Inscrever</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Categoria */}
      <Dialog open={categoryOpen} onOpenChange={(open) => { if (!open) setCategoryOpen(false); }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Adicionar Categoria</DialogTitle>
            <DialogDescription>Categorias de torneio (ex: Open, Sub-18)</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div><Label>Nome *</Label><Input value={categoryName} onChange={(e) => setCategoryName(e.target.value)} className="mt-1" placeholder="Digite o nome" /></div>
            <div><Label>Vagas (vazio = ilimitado)</Label><Input type="number" min="1" value={categoryCapacity} onChange={(e) => setCategoryCapacity(e.target.value)} className="mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCategoryOpen(false)}>Cancelar</Button>
            <Button onClick={addCategory}>Adicionar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
